import { useCallback, useEffect, useState } from "react";
import {
  INJECTION_GUIDE_SOURCE_KIND_LABEL,
  type InjectionGuideVariant,
} from "@care-by-ella/contracts";
import { UnauthorizedError } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import {
  contentVersionText,
  fmtDate,
  sourceStatus,
} from "@/lib/injection-guide-register";
import { fetchInjectionGuideRegister } from "@/lib/medication-catalogue-api";

// TES-1361 (TES-1358 §6; CP-12 2026-10-14) — the injection guide's source
// register, read only: per guide variant its official sources and what the
// weekly check last saw, Ella's content version, the medical reviewer and
// the publish date as recorded. A change at a source shows up as a review
// task above; nothing here changes the guide. The Swedish copy is proposed.

export function InjectionGuideRegister({
  reloadToken,
  onUnauthorized,
}: {
  reloadToken: number;
  onUnauthorized: () => void;
}) {
  const [variants, setVariants] = useState<InjectionGuideVariant[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setVariants((await fetchInjectionGuideRegister()).variants);
    } catch (e) {
      if (e instanceof UnauthorizedError) return onUnauthorized();
      setError(errorText(e instanceof Error ? e.message : String(e)));
    }
  }, [onUnauthorized]);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  return (
    <div
      className="border-t border-surfaceMuted pt-sm flex flex-col gap-sm"
      data-testid="injection-guide-register"
    >
      <h3 className="font-display text-lg">
        Källregister för injektionsguiden
      </h3>
      <p className="text-sm text-textSecondary max-w-2xl">
        Varje guidevariant med sina officiella källor (FASS, EMA,
        Medicininstruktioner). Kontrollen läser källorna varje måndag; en ändrad
        eller borttagen källa blir en uppgift ovan. En källa som inte går att nå
        visas här och blir ingen uppgift. Guidens text ändras aldrig
        automatiskt.
      </p>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {variants === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      {variants?.length === 0 ? (
        <p className="text-sm text-textSecondary">Registret är tomt.</p>
      ) : null}
      {variants?.map((v) => (
        <div
          key={v.id}
          className="flex flex-col gap-xs text-sm"
          data-testid={`guide-variant-${v.key}`}
        >
          <div className="flex flex-wrap items-baseline gap-sm">
            <strong>{v.label}</strong>
            {v.openReviewTaskId ? (
              <span className="text-xs text-danger">Öppen granskning</span>
            ) : null}
          </div>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-md gap-y-xs text-xs">
            <dt className="text-textSecondary">Ellas innehållsversion</dt>
            <dd>{contentVersionText(v)}</dd>
            <dt className="text-textSecondary">Medicinsk granskare</dt>
            <dd>
              {v.medicalReviewer
                ? `${v.medicalReviewer} (${fmtDate(v.medicalReviewedAt)})`
                : "ej registrerad"}
            </dd>
            <dt className="text-textSecondary">Publicerad</dt>
            <dd>{fmtDate(v.publishedAt)}</dd>
          </dl>
          {v.sources.length === 0 ? (
            <p className="text-xs text-textSecondary">Inga källor angivna.</p>
          ) : (
            <ul className="flex flex-col gap-xs">
              {v.sources.map((s) => (
                <li key={s.id} className="text-xs break-all">
                  <span className="font-medium">
                    {INJECTION_GUIDE_SOURCE_KIND_LABEL[s.kind]}
                  </span>{" "}
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    {s.url}
                  </a>
                  <br />
                  <span
                    className={
                      s.lastCheckError ? "text-danger" : "text-textSecondary"
                    }
                  >
                    {sourceStatus(s)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
